import React, { FC, useEffect, useState } from "react"
import { KTitleSpan, KSpan } from "kahuna-base-react-components"
import { handleAuthorizedRequest, summaries, users } from "../../../../../apis"
import { PerformanceStats, UserAdvanceProgress } from "../../../../../types"
import { addCommasToNumber, mapListToCamelCase } from "../../../../../utility"
import Loading from "../../../../ui/Loading"

type AdminPerformanceProps = {
  setOpenSidebar: (openSidebar: boolean) => void
  openSidebar: boolean
}

type MetricCardProps = {
  title: string
  value: string | number
  subtitle?: string
  isNew?: boolean
}

type BlurredMetricCardProps = {
  title: string
  value: string | number
  subtitle?: string
  isBlurred: boolean
  isLoading: boolean
  onClick: () => void
}

const MetricCard: FC<MetricCardProps> = ({ title, value, subtitle, isNew }) => {
  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 flex flex-col gap-2">
      <KSpan text={title} fontSize={14} lineHeight="20px" color="#666" fontWeight={500} />
      <div className="flex items-baseline gap-2">
        {isNew && <span className="text-green-600 text-sm font-medium">+</span>}
        <KTitleSpan text={String(value)} fontSize={28} lineHeight="36px" color="#111" fontWeight={600} />
      </div>
      {subtitle && <KSpan text={subtitle} fontSize={12} lineHeight="16px" color="#999" />}
    </div>
  )
}

const BlurredMetricCard: FC<BlurredMetricCardProps> = ({ title, value, subtitle, isBlurred, isLoading, onClick }) => {
  return (
    <div
      className={`bg-white rounded-xl p-6 shadow-sm border border-gray-100 flex flex-col gap-2 ${
        isBlurred ? "cursor-pointer hover:border-primary-300 transition-colors" : ""
      }`}
      onClick={isBlurred && !isLoading ? onClick : undefined}
    >
      <KSpan text={title} fontSize={14} lineHeight="20px" color="#666" fontWeight={500} />
      <div className="flex items-baseline gap-2 relative">
        {isLoading ? (
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 border-2 border-primary-300 border-t-transparent rounded-full animate-spin" />
            <KSpan text="Hesaplaniyor..." fontSize={14} color="#666" />
          </div>
        ) : isBlurred ? (
          <div className="relative">
            <KTitleSpan text="---.--%" fontSize={28} lineHeight="36px" color="#111" fontWeight={600} />
            <div
              className="absolute inset-0 backdrop-blur-sm bg-white/50 flex items-center justify-center rounded"
              style={{ width: 100 }}
            >
              <KSpan text="Click to calculate" fontSize={12} color="#666" fontWeight={500} />
            </div>
          </div>
        ) : (
          <KTitleSpan text={String(value)} fontSize={28} lineHeight="36px" color="#111" fontWeight={600} />
        )}
      </div>
      {subtitle && <KSpan text={subtitle} fontSize={12} lineHeight="16px" color="#999" />}
    </div>
  )
}

const formatNumber = (num: number | undefined | null): string => {
  if (num === undefined || num === null || isNaN(num)) {
    return "0"
  }
  if (num >= 1000000000) {
    return `${(num / 1000000000).toFixed(1)}B`
  }
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`
  }
  return addCommasToNumber(num)
}

const formatNumberWithCommas = (num: number | undefined | null): string => {
  if (num === undefined || num === null || isNaN(num)) {
    return "0"
  }
  return addCommasToNumber(num)
}

const formatCurrency = (num: number | undefined | null): string => {
  if (num === undefined || num === null || isNaN(num)) {
    return "$0"
  }
  return `$${addCommasToNumber(Math.round(num))}`
}

const formatPercentage = (num: number | undefined | null): string => {
  if (num === undefined || num === null || isNaN(num)) {
    return "-"
  }
  return `${num.toFixed(2)}%`
}

const AdminPerformance: FC<AdminPerformanceProps> = (props) => {
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState<PerformanceStats | null>(null)
  const [irrLoading, setIrrLoading] = useState(false)
  const [irrCalculated, setIrrCalculated] = useState(false)
  const [userAdvanceProgress, setUserAdvanceProgress] = useState<UserAdvanceProgress>()

  const fetchPerformanceStats = async () => {
    setLoading(true)
    await handleAuthorizedRequest(async () => {
      const response = await summaries("/performance_stats/", "GET")
      // Map snake_case to camelCase manually to handle "28d" suffix correctly
      const data = response.data
      setStats({
        totalArtists: data.total_artists,
        newArtists31d: data.new_artists_31d,
        totalTracks: data.total_tracks,
        newTracks31d: data.new_tracks_31d,
        takedownTracks: data.takedown_tracks,
        newTakedownTracks31d: data.new_takedown_tracks_31d,
        totalStreams: data.total_streams,
        newStreams31d: data.new_streams_31d,
        totalAdvances: data.total_advances,
        newAdvances31d: data.new_advances_31d,
        actualAvgRecoupMonths: data.actual_avg_recoup_months,
        estimatedAvgRecoupMonths: data.estimated_avg_recoup_months,
        totalAvgRecoupMonths: data.total_avg_recoup_months,
        totalReleases: data.total_releases,
        newReleases31d: data.new_releases_31d,
        takedownReleases: data.takedown_releases,
        newTakedownReleases31d: data.new_takedown_releases_31d,
        totalUsers: data.total_users,
        newUsers31d: data.new_users_31d
      })
      setLoading(false)
    }, "Failed to fetch performance stats")
    setLoading(false)
  }

  const calculateIRR = async () => {
    setIrrLoading(true)
    try {
      await handleAuthorizedRequest(async () => {
        // Use user_type=total to get aggregated results
        const response = await users(`/user_advance_progress/?user_type=total`, "GET")
        const results: UserAdvanceProgress[] = mapListToCamelCase(response.data.results)

        if (results.length > 0) {
          // Get IRR values from the total aggregated result
          // API returns decimal values (e.g., 0.17 = 17%), multiply by 100 for percentage display
          const totalResult = results[0]
          setUserAdvanceProgress({
            ...totalResult,
            irr12M: totalResult.irr12M ? totalResult.irr12M * 100 : 0,
            irr24M: totalResult.irr24M ? totalResult.irr24M * 100 : 0,
            irr36M: totalResult.irr36M ? totalResult.irr36M * 100 : 0
          })
        }
        setIrrCalculated(true)
      }, "IRR hesaplanamadi")
    } finally {
      setIrrLoading(false)
    }
  }

  useEffect(() => {
    fetchPerformanceStats()
  }, [])

  if (loading) {
    return (
      <div className="p-8 flex justify-center items-center min-h-[400px]">
        <Loading />
      </div>
    )
  }

  if (!stats) {
    return (
      <div className="p-8">
        <KSpan text="Failed to load performance stats" color="#666" />
      </div>
    )
  }

  return (
    <div className="px-10 pt-5">
      <div className="flex flex-row justify-between items-center mb-8">
        <div className="flex flex-row gap-2 items-center">
          <img
            className="cursor-pointer"
            src="/left-sidebar.svg"
            onClick={() => props.setOpenSidebar(!props.openSidebar)}
          />
          <KTitleSpan text="Performance" color="#111" fontSize={20} lineHeight="28px" />
        </div>
      </div>

      {/* Artist & User Metrics */}
      <div className="mb-2">
        <KSpan text="Artists & Users" fontSize={14} fontWeight={600} color="#333" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <MetricCard
          title="Total Artists"
          value={formatNumberWithCommas(stats.totalArtists)}
          subtitle="Active releases"
        />
        <MetricCard
          title="New Artists"
          value={formatNumberWithCommas(stats.newArtists31d)}
          subtitle="Last 31 days"
          isNew
        />
        <MetricCard title="Total Users" value={formatNumberWithCommas(stats.totalUsers)} subtitle="Active users" />
        <MetricCard title="New Users" value={formatNumberWithCommas(stats.newUsers31d)} subtitle="Last 31 days" isNew />
      </div>

      {/* Releases Metrics */}
      <div className="mb-2">
        <KSpan text="Releases" fontSize={14} fontWeight={600} color="#333" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <MetricCard title="Total" value={formatNumber(stats.totalReleases)} subtitle="All Takedowns" />
        <MetricCard title="New" value={formatNumber(stats.newReleases31d)} subtitle="Last 31 days" isNew />
        <MetricCard title="Takedowns" value={formatNumber(stats.takedownReleases)} subtitle="All Takedowns" />
        <MetricCard
          title="New Takedowns"
          value={formatNumber(stats.newTakedownReleases31d)}
          subtitle="Last 31 days"
          isNew
        />
      </div>

      {/* Tracks Metrics */}
      <div className="mb-2">
        <KSpan text="Songs" fontSize={14} fontWeight={600} color="#333" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <MetricCard title="Total" value={formatNumberWithCommas(stats.totalTracks)} />
        <MetricCard title="New" value={formatNumberWithCommas(stats.newTracks31d)} subtitle="Last 31 days" isNew />
        <MetricCard title="Takedowns" value={formatNumber(stats.takedownTracks)} subtitle="All Takedowns" />
        <MetricCard
          title="New Takedowns"
          value={formatNumber(stats.newTakedownTracks31d)}
          subtitle="Last 31 days"
          isNew
        />
      </div>

      {/* Advance Metrics */}
      <div className="mb-2">
        <KSpan text="Advance & Streams" fontSize={14} fontWeight={600} color="#333" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <MetricCard title="Advance Total" value={formatCurrency(stats.totalAdvances)} subtitle="All advances" />
        <MetricCard title="New Advances" value={formatCurrency(stats.newAdvances31d)} subtitle="Last 31 days" isNew />
        <MetricCard title="Total Streams" value={formatNumber(stats.totalStreams)} />
        <MetricCard title="New Streams" value={formatNumber(stats.newStreams31d)} subtitle="Last 31 days" isNew />
      </div>

      {/* Recoup Time Metrics */}
      <div className="mb-2">
        <KSpan text="Recoup Time Metrics" fontSize={14} fontWeight={600} color="#333" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <BlurredMetricCard
          title="Actual Avg. Recoup Time"
          value={`${userAdvanceProgress?.recoupActualTotal?.toFixed(1)} months`}
          subtitle="Recouped advances"
          isBlurred={!irrCalculated}
          isLoading={irrLoading}
          onClick={calculateIRR}
        />
        <BlurredMetricCard
          title="Projection Avg. Recoup Time"
          value={`${userAdvanceProgress?.recoupProjectionTotal?.toFixed(1)} months`}
          subtitle="Projection recoup time"
          isBlurred={!irrCalculated}
          isLoading={irrLoading}
          onClick={calculateIRR}
        />

        <BlurredMetricCard
          title="Overall Avg. Recoup Time"
          value={`${userAdvanceProgress?.recoupMonthTotal?.toFixed(1)} months`}
          subtitle="Actual + estimated average"
          isBlurred={!irrCalculated}
          isLoading={irrLoading}
          onClick={calculateIRR}
        />
      </div>

      {/* IRR Metrics */}
      <div className="mb-2">
        <KSpan text="IRR" fontSize={14} fontWeight={600} color="#333" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <BlurredMetricCard
          title="12M IRR"
          value={formatPercentage(userAdvanceProgress?.irr12M)}
          subtitle="Forecast"
          isBlurred={!irrCalculated}
          isLoading={irrLoading}
          onClick={calculateIRR}
        />
        <BlurredMetricCard
          title="24M IRR"
          value={formatPercentage(userAdvanceProgress?.irr24M)}
          subtitle="Forecast"
          isBlurred={!irrCalculated}
          isLoading={irrLoading}
          onClick={calculateIRR}
        />
        <BlurredMetricCard
          title="36M IRR"
          value={formatPercentage(userAdvanceProgress?.irr36M)}
          subtitle="Projection"
          isBlurred={!irrCalculated}
          isLoading={irrLoading}
          onClick={calculateIRR}
        />
      </div>
    </div>
  )
}

export default AdminPerformance
